import React, { useState, useContext } from 'react';
import { Container, Form, Button, Row, Col, Card, InputGroup, Table } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import 'react-toastify/dist/ReactToastify.css';
import './EbayImageExtractor.css';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const STORE_API_URL = process.env.REACT_APP_API_URL;

function EbayImageExtractor() {
  const { token } = useContext(AuthContext);
  
  // State for eBay image extraction
  const [ebayUrl, setEbayUrl] = useState('');
  const [ebayImages, setEbayImages] = useState([]);
  const [isExtractingImages, setIsExtractingImages] = useState(false);
  const [extractionStats, setExtractionStats] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('s-l2400.jpg');
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);

  // Function to extract eBay images
  const extractEbayImages = async () => {
    if (!ebayUrl || !ebayUrl.includes('ebay.com/itm/')) {
      toast.error('Please enter a valid eBay item URL');
      return;
    }
    
    setIsExtractingImages(true);
    setEbayImages([]);
    setExtractionStats(null);
    
    try {
      // Extract the item ID from the URL
      const itemId = ebayUrl.split('/itm/')[1].split('?')[0].split('#')[0];
      
      // Use the proxy endpoint to avoid CORS issues
      const response = await fetch(`${STORE_API_URL}/crts/proxy/ebay-images/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch eBay images');
      }
      
      const data = await response.json();
      
      if (data.images && data.images.length > 0) {
        // Filter for JPG images only
        const jpgImages = data.images.filter(url => 
          url.toLowerCase().endsWith('.jpg') || 
          url.toLowerCase().includes('.jpg?')
        );
        
        setEbayImages(jpgImages);
        
        // Set extraction stats
        setExtractionStats({
          totalFound: data.images.length,
          jpgCount: jpgImages.length,
          itemId: data.itemId,
          ebayUrl: data.ebayUrl,
          expectedCount: data.expectedCount,
          missingCount: data.missingCount,
          message: data.message
        });
        
        toast.success(`Found ${jpgImages.length} JPG images from eBay listing`);
      } else {
        toast.warn('No images found in this eBay listing');
      }
    } catch (error) {
      console.error('Error extracting eBay images:', error);
      toast.error(error.message || 'Failed to extract images from eBay');
    } finally {
      setIsExtractingImages(false);
    }
  };

  // Function to download an image
  const downloadImage = (url, filename) => {
    // Create a temporary anchor element
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `ebay-image-${Date.now()}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Function to download all images as a zip file
  const downloadAllImagesAsZip = async () => {
    if (ebayImages.length === 0) {
      toast.warn('No images to download');
      return;
    }

    setIsDownloadingZip(true);
    toast.info(`Preparing zip file with ${ebayImages.length} images...`);
    
    try {
      const zip = new JSZip();
      const imgFolder = zip.folder("ebay-images");
      
      // Create an array of promises for fetching all images
      const imagePromises = ebayImages.map(async (url, index) => {
        const response = await fetch(url);
        const blob = await response.blob();
        imgFolder.file(`ebay-image-${index + 1}.jpg`, blob);
      });
      
      // Wait for all images to be fetched and added to the zip
      await Promise.all(imagePromises);
      
      // Generate the zip file
      const zipContent = await zip.generateAsync({ type: "blob" });
      
      // Download the zip file
      const itemId = extractionStats?.itemId || "ebay-images";
      saveAs(zipContent, `ebay-images-${itemId}.zip`);
      
      toast.success('Zip file downloaded successfully');
    } catch (error) {
      console.error('Error creating zip file:', error);
      toast.error('Failed to create zip file');
    } finally {
      setIsDownloadingZip(false);
    }
  };

  // Function to change image resolution format
  const changeImageFormat = (newFormat) => {
    setSelectedFormat(newFormat);
    
    if (ebayImages.length === 0) return;
    
    // Update all image URLs to the new format
    const updatedImages = ebayImages.map(url => {
      // Replace current size format with new one
      return url.replace(/s-l\d+\.jpg/, newFormat);
    });
    
    setEbayImages(updatedImages);
    toast.info(`Changed image format to ${newFormat}`);
  };

  return (
    <Container className="my-4">
      <ToastContainer />
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>eBay Image Extractor</h2>
        <div>
          <Link to="/superadmin" className="btn btn-outline-secondary me-2">Back to Admin</Link>
        </div>
      </div>
      
      <Card className="mb-4">
        <Card.Header className="bg-primary text-white">
          Extract Images from eBay Listings
        </Card.Header>
        <Card.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>eBay Item URL</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Enter eBay item URL (e.g., https://www.ebay.com/itm/123456789)"
                  value={ebayUrl}
                  onChange={(e) => setEbayUrl(e.target.value)}
                />
                <Button 
                  variant="primary" 
                  onClick={extractEbayImages}
                  disabled={isExtractingImages}
                >
                  {isExtractingImages ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Extracting...
                    </>
                  ) : (
                    'Extract Images'
                  )}
                </Button>
              </InputGroup>
              <Form.Text className="text-muted">
                Paste the full URL of an eBay listing to extract all images.
              </Form.Text>
            </Form.Group>
          </Form>
        </Card.Body>
      </Card>
      
      {extractionStats && (
        <Card className="mb-4">
          <Card.Header className="bg-info text-white">
            Extraction Results
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover size="sm">
              <tbody>
                <tr>
                  <th>eBay Item ID</th>
                  <td>
                    <a href={extractionStats.ebayUrl} target="_blank" rel="noopener noreferrer">
                      {extractionStats.itemId}
                    </a>
                  </td>
                </tr>
                <tr>
                  <th>Images Found</th>
                  <td>{extractionStats.jpgCount} JPG images</td>
                </tr>
                {extractionStats.expectedCount && (
                  <tr>
                    <th>Expected Images</th>
                    <td>
                      {extractionStats.expectedCount}
                      {extractionStats.missingCount > 0 && (
                        <span className="text-warning ms-2">
                          (Missing {extractionStats.missingCount})
                        </span>
                      )}
                    </td>
                  </tr>
                )}
                {extractionStats.message && (
                  <tr>
                    <th>Status</th>
                    <td>{extractionStats.message}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
      
      {ebayImages.length > 0 && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3>Found {ebayImages.length} Images</h3>
            <div>
              <div className="btn-group me-2">
                <Button 
                  variant={selectedFormat === 's-l2400.jpg' ? 'primary' : 'outline-primary'} 
                  size="sm"
                  onClick={() => changeImageFormat('s-l2400.jpg')}
                >
                  High Resolution (2400px)
                </Button>
                <Button 
                  variant={selectedFormat === 's-l1600.jpg' ? 'primary' : 'outline-primary'} 
                  size="sm"
                  onClick={() => changeImageFormat('s-l1600.jpg')}
                >
                  Medium Resolution (1600px)
                </Button>
                <Button 
                  variant={selectedFormat === 's-l1000.jpg' ? 'primary' : 'outline-primary'} 
                  size="sm"
                  onClick={() => changeImageFormat('s-l1000.jpg')}
                >
                  Low Resolution (1000px)
                </Button>
              </div>
              <Button 
                variant="primary" 
                size="sm"
                onClick={downloadAllImagesAsZip}
                disabled={isDownloadingZip}
              >
                {isDownloadingZip ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Creating Zip...
                  </>
                ) : (
                  'Download as Zip'
                )}
              </Button>
            </div>
          </div>
          
          <Row>
            {ebayImages.map((url, index) => (
              <Col lg={3} md={4} sm={6} key={index} className="mb-4">
                <Card className="image-card h-100">
                  <div className="image-preview">
                    <a href={url} target="_blank" rel="noopener noreferrer">
                    <img src={url} alt={`eBay item ${index + 1}`} className="img-fluid" />
                    </a>
                  </div>
                  <Card.Footer className="p-2">
                    <Form.Control
                      type="text"
                      value={url}
                      size="sm"
                      readOnly
                      onClick={(e) => e.target.select()}
                    />
                  </Card.Footer>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}
    </Container>
  );
}

export default EbayImageExtractor; 